/**
 * UI-related constants to avoid magic numbers and strings
 */

import type { FinishReason } from '@/types/messages';

export const PREVIEW_LINES_DEFAULT = 6;

/** Preview lines shown for tool call results in the Tools component */
export const TOOL_RESULT_PREVIEW_LINES = 4;

/** Animation stagger delay (ms) per dot in the SubSession flow wave */
export const DOT_WAVE_DELAY_MS = 120;

/**
 * Animation delays (ms) for the three-dot bounce indicator shown next
 * to an actively-running tool call inside a SubSession event feed.
 * The first dot has no delay (0), so only the non-zero offsets need names.
 */
export const BOUNCE_DOT_DELAY_1_MS = 150;
export const BOUNCE_DOT_DELAY_2_MS = 300;

/** Max-height for auto-scrolling event feed containers */
export const EVENTS_SCROLL_MAX_HEIGHT = '8rem';

/** Max characters for the auto-generated thread title */
export const THREAD_TITLE_MAX_LENGTH = 50;

/**
 * Valkey stream start ID — mirrors the backend STREAM_START_ID constant.
 * Used as the initial lastEventId when subscribing to or reconnecting a
 * stream from the very beginning.
 */
export const STREAM_START_ID = '0-0';

/**
 * How often (ms) the streaming poller falls back to polling the REST
 * messages endpoint while waiting for a terminal status.
 */
export const POLL_INTERVAL_MS = 3000;

/**
 * Maximum number of REST polling attempts before giving up.
 * At POLL_INTERVAL_MS = 3 s this gives a 90 s hard deadline before
 * the poller forces the message to 'error' and stops.  This prevents
 * infinite spinning when a Postgres message is stuck in STREAMING
 * with no Valkey state (Bug 1 scenario: consume_stream_to_valkey
 * crashes before any state is written and before the Valkey TTL
 * kicks in to trigger fail_if_stuck via GET /state).
 */
export const POLL_MAX_ATTEMPTS = 30;

/**
 * Re-reads of a thread while an answer card is pending, at POLL_INTERVAL_MS.
 * review_reasoner's card agent runs after the turn's stream closed, for up to
 * its ANSWER_CARD_TIMEOUT_SECONDS (900 s); 330 reads wait 990 s. Past the cap
 * the UI stops waiting and shows no card.
 */
export const ANSWER_CARD_POLL_MAX_ATTEMPTS = 330;

/**
 * Minimum pause (ms) between applying restored backend state and starting
 * the SSE reconnect.  React needs at least one paint cycle to flush the
 * restored content into the DOM; without this delay the new SSE events
 * arrive before the paint and can overwrite the just-restored text.
 */
export const PAINT_DELAY_MS = 200;

/**
 * Pause (ms) before re-attaching to a message whose stream closed without
 * ending it, doubled per failed attempt up to the max.
 */
export const RECONNECT_BACKOFF_MS = 1000;
export const RECONNECT_BACKOFF_MAX_MS = 30000;

/**
 * `error_source` of the ERROR frame that ends one stream connection at its
 * deadline — mirrors the backend ErrorSource.STREAM_TIMEOUT. The message
 * goes on; the client reconnects.
 */
export const STREAM_TIMEOUT_ERROR_SOURCE = 'stream_timeout';

/** Number of skeleton thread-card items in the ReVeal page skeleton */
export const SKELETON_SIDEBAR_ITEM_COUNT = 5;

/**
 * Per-item animation stagger delay (ms) for skeleton loading cards.
 * Applied as `i * SKELETON_STAGGER_DELAY_MS` (i is 1-based).
 */
export const SKELETON_STAGGER_DELAY_MS = 80;

/**
 * Button metrics, taken from reMap's toolbars so a compact button is the
 * same object across the app rather than a taller one of its own.
 */
export const COMPACT_BUTTON_CLASS = 'h-8 !text-xs';

export const SIDEBAR_WIDTH = 'w-96';
export const SIDEBAR_WIDTH_PX = 384;

// Sub-session detail sidebar (right panel showing agent inner workings)
export const SUBSESSION_SIDEBAR_W_CLASS = 'w-[480px]';
export const SUBSESSION_SIDEBAR_MR_CLASS = 'mr-[480px]';

export const PERCENTAGE_MULTIPLIER = 100;

export const TODO_KEY_CONTENT_LENGTH = 30;

export const DEFAULT_PLAN_ID = 'main';

export const PLAN_OPERATION_CREATE = 'create';
export const PLAN_OPERATION_READ = 'read';

// Plan execution
export const EXECUTE_PLAN_COMMAND_PREFIX = 'execute plan';
export const UNKNOWN_PLAN_ID = 'unknown';
export const NODE_ENV_DEVELOPMENT = 'development';

// Icon sizes (in pixels)
export const ICON_SIZE_SMALL = 16;
export const ICON_SIZE_MEDIUM = 20;
export const ICON_SIZE_LARGE = 24;

// LLM finish-reason labels shown in message banners.
// max_tokens (Anthropic/OpenAI) and length (other providers) map to the same
// label intentionally — both mean the model was cut off at the token limit.
export const FINISH_REASON_LABELS: Record<FinishReason, string> = {
  max_tokens: 'Content limit reached',
  length: 'Content limit reached',
  content_filter: 'Response blocked by content filter',
  stop_sequence: 'Stopped at sequence boundary',
  unknown: 'Response ended unexpectedly',
};

